import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { roleApi } from '@/services/roleApi';
import { RoleFilters } from '@/types';
import SimpleSelect from '@/components/common/SimpleSelect';

const ROLE_TYPE_FILTER_OPTIONS = [
  { value: '', label: 'All Roles' },
  { value: 'true', label: 'System Roles' },
  { value: 'false', label: 'Custom Roles' },
];
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Shield,
  Users,
  Lock,
  AlertCircle,
} from 'lucide-react';

// Only Super Administrator role is locked from editing (it always has all permissions)
const NON_EDITABLE_ROLES = ['Super Administrator'];

// Helper function to check if a role can be edited
const isRoleEditable = (role: { name: string; isSystemRole: boolean }) => {
  return !NON_EDITABLE_ROLES.includes(role.name);
};

export default function RoleList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [filters, setFilters] = useState<RoleFilters>({
    search: '',
    isSystemRole: undefined,
  });

  // Fetch roles
  const { data, isLoading } = useQuery({
    queryKey: ['roles', filters, page, limit],
    queryFn: () => roleApi.getAllRoles(filters, page, limit),
    staleTime: 10 * 60 * 1000, // 10 minutes - roles rarely change
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: roleApi.deleteRole,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
  });

  const handleDelete = async (id: string, name: string, isSystemRole: boolean) => {
    if (isSystemRole) {
      alert('System roles cannot be deleted');
      return;
    }

    if (
      window.confirm(
        `Are you sure you want to delete role "${name}"? This action cannot be undone.`
      )
    ) {
      try {
        await deleteMutation.mutateAsync(id);
        alert('Role deleted successfully');
      } catch (error: any) {
        alert(error.response?.data?.message || 'Failed to delete role');
      }
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
  };

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Roles</h1>
            <p className="text-gray-600 mt-1">Manage user roles and permissions</p>
          </div>
          <button
            onClick={() => navigate('/roles/create')}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-500 text-white rounded-lg hover:from-purple-700 hover:to-purple-600 transition-all shadow-lg hover:shadow-xl"
          >
            <Plus className="h-5 w-5" />
            Add Role
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search roles..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            <SimpleSelect
              value={filters.isSystemRole === undefined ? '' : filters.isSystemRole.toString()}
              onChange={(v) =>
                setFilters({
                  ...filters,
                  isSystemRole: v === '' ? undefined : v === 'true',
                })
              }
              options={ROLE_TYPE_FILTER_OPTIONS}
              placeholder="All Roles"
              aria-label="Filter by role type"
              wrapperClassName="w-full sm:w-44"
              className="focus:!ring-purple-500 focus:!border-purple-500"
            />

            <button
              type="submit"
              className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Apply Filters
            </button>
          </form>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            </div>
          ) : data?.roles.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <Shield className="h-16 w-16 mb-4 text-gray-300" />
              <p className="text-lg font-medium">No roles found</p>
              <p className="text-sm">Create your first custom role to get started</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Role
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Permissions
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Users
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {data?.roles.map((role) => (
                      <tr key={role.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 bg-purple-100 rounded-lg flex items-center justify-center">
                              <Shield className="h-6 w-6 text-purple-600" />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                                {role.name}
                                {role.isSystemRole && (
                                  <Lock className="h-3 w-3 text-gray-400" title="System Role" />
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900 max-w-md truncate">
                            {role.description || '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <AlertCircle className="h-4 w-4 text-gray-400" />
                            <span className="text-sm font-medium text-gray-900">
                              {role.permissions?.length || 0}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4 text-gray-400" />
                            <span className="text-sm font-medium text-gray-900">
                              {role._count?.assignedUsers || 0}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {role.isSystemRole ? (
                            <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                              System
                            </span>
                          ) : (
                            <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                              Custom
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            <button
                                  onClick={() => isRoleEditable(role) && navigate(`/roles/edit/${role.id}`)}
                                  className={`p-2 rounded-lg transition-colors ${
                                    !isRoleEditable(role)
                                      ? 'text-gray-400 cursor-not-allowed opacity-50'
                                      : 'text-blue-600 hover:bg-blue-50'
                                  }`}
                                  title={!isRoleEditable(role) ? "This system role cannot be edited" : "Edit role"}
                                  disabled={!isRoleEditable(role)}
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                            <button
                              onClick={() => handleDelete(role.id, role.name, role.isSystemRole)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Delete role"
                              disabled={role.isSystemRole}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {data && data.pagination.totalPages > 1 && (
                <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
                  <div className="text-sm text-gray-700">
                    Showing page {data.pagination.page} of {data.pagination.totalPages} (
                    {data.pagination.total} total)
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setPage(page + 1)}
                      disabled={page === data.pagination.totalPages}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
